import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import useHiddenModules from "@/lib/useHiddenModules";
import { useOpenTaskCounts, OpenTasksBadge } from "@/components/OpenTasksBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Users, Search, ArrowRight, MapPin, Filter } from "lucide-react";
import TenantWizard from "@/components/wizards/TenantWizard";
import { fmtEur, COLOR, COLOR_LABEL, DOT } from "@/lib/format";

const DUNNING = { herinnering_1: "1e herinnering", herinnering_2: "2e herinnering", aanmaning: "Aanmaning" };
const RISK_CLASS = { green: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700" };

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [q, setQ] = useState("");
  const [catalog, setCatalog] = useState({ debtor_statuses: [], risk_profiles: [] });
  const [statusFilter, setStatusFilter] = useState([]);
  const navigate = useNavigate();
  const noPay = useHiddenModules().includes("tab-betalingen");
  const taskCounts = useOpenTaskCounts(noPay);
  const [searchParams] = useSearchParams();

  const load = () => api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  useEffect(() => { const s = searchParams.get("status"); if (s) setStatusFilter([s]); }, [searchParams]);

  const toggleFilter = (s) => setStatusFilter((f) => f.includes(s) ? f.filter((x) => x !== s) : [...f, s]);
  const riskClass = (name) => RISK_CLASS[(catalog.risk_profiles || []).find((p) => p.name === name)?.color] || "bg-slate-100 text-slate-700";

  const filtered = tenants.filter((t) => {
    const matchesText = `${t.first_name} ${t.last_name} ${t.naw_city} ${t.client_name}`.toLowerCase().includes(q.toLowerCase());
    const matchesStatus = statusFilter.length === 0 || statusFilter.every((s) => (t.statuses || []).includes(s));
    return matchesText && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#0F1E3D]">Debiteuren</h1>
          <p className="text-slate-500">Klik op een rij voor het volledige dossier.</p>
        </div>
        <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setWizard(true)} data-testid="add-tenant-wizard-button">
          <Plus className="h-4 w-4 mr-2" /> Nieuwe debiteur
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Zoek op naam, plaats of opdrachtgever" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenant-search-input" />
        </div>
        {(catalog.debtor_statuses || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="tenant-status-filter">
            <span className="text-xs text-slate-400 flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> Filter op status:</span>
            {catalog.debtor_statuses.map((s) => {
              const on = statusFilter.includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleFilter(s)} data-testid={`tenant-filter-${s}`}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                  {s}
                </button>
              );
            })}
            {statusFilter.length > 0 && <button type="button" onClick={() => setStatusFilter([])} className="text-xs text-slate-400 hover:text-slate-700 underline" data-testid="tenant-filter-clear">wissen</button>}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-4">Nog geen debiteuren gevonden.</p>
          <Button onClick={() => setWizard(true)} className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white"><Plus className="h-4 w-4 mr-2" /> Debiteur toevoegen</Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <div key={t.id} onClick={() => navigate(`/app/huurders/${t.id}`)} data-testid={`tenant-row-${t.id}`}
              className="flex items-center justify-between gap-4 p-4 rounded-xl border bg-white hover:shadow-md hover:border-slate-300 transition-all cursor-pointer">
              <div className="flex items-center gap-4 min-w-0">
                {!noPay && <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${DOT[t.status_color]}`} />}
                <div className="h-10 w-10 rounded-full bg-[#0F1E3D] text-[#C9A227] flex items-center justify-center font-semibold text-sm shrink-0">
                  {t.first_name?.[0]}{t.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{t.first_name} {t.last_name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t.property_label || t.naw_city || "—"}{t.client_name && ` • ${t.client_name}`}</div>
                  {(t.dunning_stage || t.risk_profile || (t.statuses || []).length > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`tenant-row-badges-${t.id}`}>
                      {t.dunning_stage && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[11px]" title="Automatisch toegekend in de aanmaningsflow">{DUNNING[t.dunning_stage] || t.dunning_stage}</Badge>}
                      {t.risk_profile && <Badge className={`${riskClass(t.risk_profile)} text-[11px]`}>Risico: {t.risk_profile}</Badge>}
                      {(t.statuses || []).map((s) => <Badge key={s} variant="outline" className="border-blue-200 text-blue-700 text-[11px]">{s}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-5">
                {!noPay && <div className="text-right hidden sm:block"><div className="text-xs text-slate-400">Openstaand</div><div className="font-semibold text-slate-800">{fmtEur(t.outstanding)}</div></div>}
                {!noPay && <div className="text-right hidden sm:block"><div className="text-xs text-slate-400">Te laat</div><div className={`font-semibold ${t.overdue_amount > 0 ? "text-red-600" : "text-slate-500"}`}>{fmtEur(t.overdue_amount)}</div></div>}
                {noPay ? <OpenTasksBadge n={taskCounts[t.id] || 0} tid={t.id} /> : <Badge className={COLOR[t.status_color]}>{COLOR_LABEL[t.status_color]}</Badge>}
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      <TenantWizard open={wizard} onOpenChange={setWizard} onSaved={() => load()} />
    </div>
  );
}
